import React, { FC, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Pagination,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';

// Explicit types for work order and AE PPhsEDTO
type AePPhsEDTO = {
  locationCode: string;
  craftCode: string;
  shop: string;
  sortCode: string;
  statusCode: string;
};

type WorkOrderData = {
  proposal: string;
  category: string;
  description: string;
  orderType: string;
  statusCode: string;
  aePPhsEDTOList: AePPhsEDTO[];
};

export type WorkOrderResponse = {
    response:{
  value: {
    data: WorkOrderData;
  }};
};

type WorkOrderDialogSuccessReportProps = {
  open: boolean;
  onClose: () => void;
  workOrders: WorkOrderResponse[];
};

const WorkOrderDialogSuccessReport: FC<WorkOrderDialogSuccessReportProps> = ({
  open,
  onClose,
  workOrders,
}) => {
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [open, workOrders]);

  const total = workOrders.length;
  const current = workOrders[page - 1]?.response?.value?.data;

  const handleChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span>
          Created Work Orders
          {total > 1 && (
            <Typography variant="subtitle2" component="span" sx={{ marginLeft: 2 }}>
              {page} of {total}
            </Typography>
          )}
        </span>
        <IconButton
          aria-label="close"
          onClick={onClose}
          size="large"
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {total === 0 || !current ? (
          <Typography>No work orders created.</Typography>
        ) : (
          <Box>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  AiM Work Order: {current.proposal}
                </Typography>
                <Typography>
                  <strong>Category:</strong> {current.category}
                </Typography>
                <Typography>
                  <strong>Description:</strong> {current.description}
                </Typography>
                <Typography>
                  <strong>Order Type:</strong> {current.orderType}
                </Typography>
                <Typography>
                  <strong>Status Code:</strong> {current.statusCode}
                </Typography>
                <Box mt={2}>
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>
                        AE PPhsEDTO List ({current.aePPhsEDTOList?.length || 0})
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {current.aePPhsEDTOList && current.aePPhsEDTOList.length > 0 ? (
                        current.aePPhsEDTOList.map((item, i) => (
                          <Box key={i} mb={1} pl={1} borderLeft={1} borderColor="grey.300">
                            <Typography>
                              <strong>Location Code:</strong> {item.locationCode}
                            </Typography>
                            <Typography>
                              <strong>Craft Code:</strong> {item.craftCode}
                            </Typography>
                            <Typography>
                              <strong>Shop:</strong> {item.shop}
                            </Typography>
                            <Typography>
                              <strong>Sort Code:</strong> {item.sortCode}
                            </Typography>
                            <Typography>
                              <strong>Status Code:</strong> {item.statusCode}
                            </Typography>
                          </Box>
                        ))
                      ) : (
                        <Typography>No AE PPhsEDTO items.</Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                </Box>
              </CardContent>
            </Card>
            {
            total > 1 &&
            (
              <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
                <Pagination
                  count={total}
                  page={page}
                  onChange={handleChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )
            }
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WorkOrderDialogSuccessReport;